import { TutorialStep } from '../../../../../types/alice-bob';
import { setTutorialStep } from '../../../../backend/store/tutorial';
import Modules from '../../gui/modules/Modules';
import { CHAPTERS_CONTENT } from './chapters-content';
import Page from './page/Page';
import TextPanel, { type ChapterPageContent } from './textPanel/TextPanel';

const DIRECTIONS = {
    LEFT: -1,
    RIGHT: 1,
};

export type ChapterPage = {
    id: string;
    content: ChapterPageContent[];
};
export type Chapters = {
    id: number;
    prefix: string;
    pages: Array<ChapterPage>;
};

export const CHAPTERS: Chapters[] = [
    {
        id: 1,
        prefix: 'comic/chapter_1/',
        pages: [
            { id: '1-0', content: CHAPTERS_CONTENT['1-0'] },
            { id: '1-1', content: CHAPTERS_CONTENT['1-1'] },
            { id: '1-2', content: CHAPTERS_CONTENT['1-2'] },
            { id: '1-3', content: CHAPTERS_CONTENT['1-3'] },
            { id: '1-4', content: CHAPTERS_CONTENT['1-4'] },
            { id: '1-5', content: CHAPTERS_CONTENT['1-5'] },
            { id: '1-6', content: CHAPTERS_CONTENT['1-6'] },
        ],
    },
    {
        id: 2,
        prefix: 'comic/chapter_2/',
        pages: [
            { id: '2-1', content: CHAPTERS_CONTENT['2-1'] },
            { id: '2-2', content: CHAPTERS_CONTENT['2-2'] },
            { id: '2-3', content: CHAPTERS_CONTENT['2-3'] },
            { id: '2-4', content: CHAPTERS_CONTENT['2-4'] },
            { id: '2-5', content: CHAPTERS_CONTENT['2-5'] },
            { id: '2-6', content: CHAPTERS_CONTENT['2-6'] },
        ],
    },
    {
        id: 3,
        prefix: 'comic/chapter_3/',
        pages: [
            { id: '3-1', content: CHAPTERS_CONTENT['3-1'] },
            { id: '3-2', content: CHAPTERS_CONTENT['3-2'] },
            { id: '3-3', content: CHAPTERS_CONTENT['3-3'] },
            { id: '3-4', content: CHAPTERS_CONTENT['3-4'] },
            { id: '3-5', content: CHAPTERS_CONTENT['3-5'] },
            { id: '3-6', content: CHAPTERS_CONTENT['3-6'] },
            { id: '3-7', content: CHAPTERS_CONTENT['3-7'] },
        ],
    },
    {
        id: 4,
        prefix: 'comic/chapter_4/',
        pages: [
            { id: '4-1', content: CHAPTERS_CONTENT['4-1'] },
            { id: '4-2', content: CHAPTERS_CONTENT['4-2'] },
            { id: '4-3', content: CHAPTERS_CONTENT['4-3'] },
            { id: '4-4', content: CHAPTERS_CONTENT['4-4'] },
            { id: '4-5', content: CHAPTERS_CONTENT['4-5'] },
            { id: '4-6', content: CHAPTERS_CONTENT['4-6'] },
        ],
    },
    {
        id: 5,
        prefix: 'comic/chapter_5/',
        pages: [
            { id: '5-1', content: CHAPTERS_CONTENT['5-1'] },
            { id: '5-2', content: CHAPTERS_CONTENT['5-2'] },
            { id: '5-3', content: CHAPTERS_CONTENT['5-3'] },
            { id: '5-4', content: CHAPTERS_CONTENT['5-4'] },
            { id: '5-5', content: CHAPTERS_CONTENT['5-5'] },
            { id: '5-6', content: CHAPTERS_CONTENT['5-6'] },
        ],
    },
    {
        id: 6,
        prefix: 'comic/chapter_6/',
        pages: [
            { id: '6-1', content: CHAPTERS_CONTENT['6-1'] },
            { id: '6-2', content: CHAPTERS_CONTENT['6-2'] },
            { id: '6-3', content: CHAPTERS_CONTENT['6-3'] },
            { id: '6-4', content: CHAPTERS_CONTENT['6-4'] },
            { id: '6-5', content: CHAPTERS_CONTENT['6-5'] },
            { id: '6-6', content: CHAPTERS_CONTENT['6-6'] },
            { id: '6-7', content: CHAPTERS_CONTENT['6-7'] },
        ],
    },
];

export default class Chapter extends Phaser.GameObjects.Container {
    private id: number;
    private pages: Page[] = [];
    private currentPageIndex: number = -1;
    private storyPanel: any;

    private btnPrev!: Phaser.GameObjects.Image;
    private btnNext!: Phaser.GameObjects.Image;
    private close!: Phaser.GameObjects.Image;

    private textPanel!: TextPanel;

    constructor(scene: Phaser.Scene, x: number, y: number, id: number, storyPanel: any) {
        super(scene, x, y);

        this.id = id;
        this.storyPanel = storyPanel;

        this.setupPages();

        this.textPanel = new TextPanel(this.scene, 0, 0);
        this.add(this.textPanel);

        this.setupButtons();
        this.setupClose();
        this.setupListeners();
    }

    static get DIRECTIONS() {
        return DIRECTIONS;
    }

    setupPages() {
        let width = this.scene.cameras.main.width;
        let config = CHAPTERS.find((chapter) => chapter.id === this.id);
        if (!config) {
            console.warn(`Chapter with id ${this.id} not found`);
            return;
        }

        let pages = config.pages;

        for (let i = 0; i < pages.length; i++) {
            let x = (i + 1) * width;
            let y = 0;

            const prefix = `comic/chapter_${this.id}/`;
            let frame = `${prefix}${pages[i].id}`;

            let page = new Page(this.scene, x, y, frame);

            page.setInteractive({ useHandCursor: true });
            page.on('pointerdown', () => this.movePages(DIRECTIONS.LEFT));

            this.add(page);

            this.pages.push(page);
        }
    }

    setupButtons() {
        let x = -this.scene.cameras.main.width / 2 + 50;
        let y = 0;

        this.btnPrev = this.scene.add.image(x, y, 'atlas', 'comic/btn_prev');
        this.btnPrev.setInteractive({ useHandCursor: true });
        this.btnPrev.on('pointerdown', () => this.movePages(DIRECTIONS.RIGHT));
        this.add(this.btnPrev);

        x = this.scene.cameras.main.width / 2 - 50;

        this.btnNext = this.scene.add.image(x, y, 'atlas', 'comic/btn_next');
        this.btnNext.setInteractive({ useHandCursor: true });
        this.btnNext.on('pointerdown', () => this.movePages(DIRECTIONS.LEFT));
        this.add(this.btnNext);
    }

    setupClose() {
        let x = this.scene.cameras.main.width / 2 - 50;
        let y = -540;
        let frame = 'icons/close';

        this.close = this.scene.add.image(x, y, 'atlas', frame);
        this.add(this.close);

        this.close.setInteractive({ useHandCursor: true });
        this.close.once('pointerdown', this.onClose, this);
    }

    setupListeners(): void {
        this.scene.events.on('CHAPTER_MOVE_RIGHT_OR_CLOSE', this.movePages, this);
    }

    async movePages(direction: number) {
        this.hideButtons();
        this.textPanel.dispose();

        if (this.currentPageIndex + 1 >= this.pages.length && direction == DIRECTIONS.LEFT) {
            this.onClose();
            // await this.storyPanel.hide();
            return;
        }

        let jobs = [];
        for (let page of this.pages) jobs.push(page.moveTo(direction));
        await Promise.all(jobs);

        this.currentPageIndex += direction * -1;

        setTutorialStep(this.currentPageIndex + TutorialStep.PLAYER_INITIALIZED + 1);

        this.updateButtons();
        this.showCaptions();
    }

    public showCaptions(): void {
        const chapter = CHAPTERS.find((ch) => ch.id === this.id);
        if (!chapter) return;

        const currentPage = chapter.pages[this.currentPageIndex];
        if (!currentPage) return;
        this.textPanel.showCaptions(currentPage.content);
    }

    onClose() {
        this.scene.events.emit(Modules.EVENTS.HIDE_STORY);
        this.textPanel.dispose();
        this.emit('close');
        this.destroy();
    }

    hideButtons() {
        this.btnNext.setVisible(false);
        this.btnPrev.setVisible(false);
    }

    updateButtons() {
        if (this.currentPageIndex > 0) {
            this.btnPrev.setVisible(true);
        }

        if (this.currentPageIndex + 1 < this.pages.length) {
            this.btnNext.setVisible(true);
        }
    }

    destroy(fromScene?: boolean): void {
        this.scene.events.off('CHAPTER_MOVE_RIGHT_OR_CLOSE', this.movePages, this);
        super.destroy(fromScene);
    }
}
